/**
 * Apa yang layak disebut "pemberitahuan baru" — MURNI, tanpa React dan tanpa `localStorage`.
 *
 * DUA JENIS YANG SIFATNYA BERBEDA, dan perbedaan itu menentukan cara menghitungnya:
 *
 * - **Pengajuan koreksi** punya KEADAAN. Ia `PENDING` sampai seseorang memutuskannya, jadi ia
 *   dihitung apa adanya; membukanya tidak membuatnya selesai, dan angkanya memang harus bertahan.
 * - **Tugas selesai** adalah PERISTIWA. Tidak ada keadaan "sudah dibaca" di server, jadi
 *   menghitungnya apa adanya menghasilkan angka yang tidak pernah kembali nol — lencana yang
 *   selalu menyala persis sama tidak berartinya dengan lencana yang tidak pernah menyala.
 *
 * Karena itu penyelesaian dihitung SEJAK TERAKHIR DAFTARNYA DIBUKA. Penandanya hidup di peramban
 * masing-masing orang (lihat `terakhirDilihat` di lapisan UI), bukan di server: menyimpannya di
 * server berarti menambah kolom dan migrasi untuk sebuah kenyamanan, dan dua manajer yang
 * membuka daftar yang sama akan saling menghapus tanda satu sama lain.
 *
 * Akibat yang JUJUR dari pilihan itu: tandanya per-peramban. Manajer yang membuka dari ponsel
 * lalu dari laptop akan melihat daftar "baru" dua kali. Itu ongkos yang disengaja, dan lebih
 * kecil daripada migrasi untuk lencana.
 */

import type { FieldTask } from '../api/types';

/**
 * Kalau belum pernah membuka daftarnya, seberapa jauh ke belakang yang dianggap "baru".
 *
 * 24 jam, bukan sejak awal waktu. Manajer yang pertama kali membuka lonceng ini tidak terbantu
 * oleh daftar berisi setiap tugas yang pernah selesai; yang ia cari adalah apa yang terjadi
 * selagi ia tidak melihat.
 */
export const JENDELA_AWAL_JAM = 24;

export function ambangAwal(sekarang: Date): string {
  return new Date(sekarang.getTime() - JENDELA_AWAL_JAM * 3_600_000).toISOString();
}

/**
 * Tugas yang selesai SESUDAH `sejak`, terbaru lebih dulu.
 *
 * Tugas tanpa `completedAt` dibuang — bukan diperlakukan sebagai baru. Status `completed` tanpa
 * stempel waktu berarti kita tidak tahu KAPAN, dan menebaknya "baru saja" akan menaruh tugas lama
 * di puncak daftar setiap kali halamannya dibuka.
 */
export function tugasSelesaiSejak(
  tugas: readonly FieldTask[],
  sejakIso: string,
): readonly FieldTask[] {
  const sejak = Date.parse(sejakIso);
  if (Number.isNaN(sejak)) return [];

  return tugas
    .filter((t) => {
      if (t.status !== 'completed') return false;
      if (!t.completedAt) return false;
      const selesai = Date.parse(t.completedAt);
      return !Number.isNaN(selesai) && selesai > sejak;
    })
    .slice()
    .sort((a, b) => Date.parse(b.completedAt!) - Date.parse(a.completedAt!));
}

/** Angka yang muncul di lencana: keduanya dijumlah, karena loncengnya cuma satu. */
export function jumlahPemberitahuan(koreksiMenunggu: number, tugasBaruSelesai: number): number {
  return Math.max(0, Math.floor(koreksiMenunggu)) + Math.max(0, Math.floor(tugasBaruSelesai));
}

/** Bagian yang datanya tidak berhasil diambil — dinamai dalam bahasa pembacanya. */
export type BagianPemberitahuan = 'pengajuan koreksi' | 'tugas selesai';

export interface RingkasanPemberitahuan {
  readonly jumlah: number;
  /**
   * Benar HANYA bila kita benar-benar tahu tidak ada apa-apa.
   *
   * ADA KARENA VERSI PERTAMA BERBOHONG KE ARAH YANG MENENANGKAN. Layar pemberitahuan dulu
   * menyatakan "Tidak ada pemberitahuan baru" bila salah satu dari dua permintaannya gagal, asalkan
   * tidak keduanya — sehingga daftar pengajuan yang gagal diambil terbaca sebagai "tidak ada
   * pengajuan". Manajer yang membacanya menyimpulkan tidak ada yang menunggu keputusannya, dan
   * izin sakit yang sesungguhnya menunggu tidak pernah terlihat.
   *
   * Nol yang JUJUR menuntut dua hal sekaligus: tidak ada isi, DAN tidak ada bagian yang gagal.
   */
  readonly kosongSungguhan: boolean;
  readonly bagianGagal: readonly BagianPemberitahuan[];
}

export function ringkasanPemberitahuan(input: {
  readonly koreksiGagal: boolean;
  readonly tugasGagal: boolean;
  readonly jumlahKoreksi: number;
  readonly jumlahTugas: number;
}): RingkasanPemberitahuan {
  const bagianGagal: BagianPemberitahuan[] = [];
  if (input.koreksiGagal) bagianGagal.push('pengajuan koreksi');
  if (input.tugasGagal) bagianGagal.push('tugas selesai');

  const jumlah = jumlahPemberitahuan(input.jumlahKoreksi, input.jumlahTugas);
  return { jumlah, kosongSungguhan: jumlah === 0 && bagianGagal.length === 0, bagianGagal };
}

/** Kalimat untuk bagian yang gagal — menyebut APA yang tidak diketahui, bukan "terjadi kesalahan". */
export function kalimatBagianGagal(bagian: readonly BagianPemberitahuan[]): string | null {
  if (bagian.length === 0) return null;
  return `Daftar ${bagian.join(' dan ')} tidak dapat dimuat, jadi mungkin ada yang belum terlihat di sini.`;
}
